import React from "react";

import { css } from "@compiled/react";
import {
    UniformSlot,
    UniformSlotWrapperComponentProps
} from "@uniformdev/canvas-react";

import { media } from "@atlaskit/primitives/responsive";

import { ListItemNumber } from "./list-item-number.tsx";

import type { spaceTokenNumberValue } from "../../../utils";

export type ListAs = "ul" | "ol" | "dl";
type ViewportSize = "mobile" | "tablet" | "desktop";
type ResponsiveGapOptions = Record<ViewportSize, spaceTokenNumberValue>;

export interface UniformListV2Props {
    listType: ListAs;
    gapv2?: ResponsiveGapOptions;
}

const UniformListV2: React.FC<UniformListV2Props> = ({
    listType,
    gapv2 = {
        mobile: "0"
    }
}) => {
    const gapObject: ResponsiveGapOptions = {
        mobile: gapv2.mobile ?? "0",
        tablet: gapv2?.tablet ?? gapv2.mobile ?? "0",
        desktop: gapv2?.desktop ?? gapv2?.tablet ?? gapv2.mobile ?? "0"
    };

    const listStyle = css({
        display: "flex",
        flexDirection: "column",
        gap: "var(--ds-space-" + gapObject.mobile + ")",
        [media.above.sm]: {
            gap: "var(--ds-space-" + gapObject.tablet + ")"
        },
        [media.above.md]: {
            gap: "var(--ds-space-" + gapObject.desktop + ")"
        }
    });

    const zeroMarginPadding = css({
        margin: 0,
        padding: 0
    });

    const resetListStyles = css({
        // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
        ["& ul"]: {
            listStyleType: "disc",
            // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
            ["& ul"]: {
                listStyleType: "circle",
                // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
                ["& ul"]: {
                    listStyleType: "square"
                }
            }
        }
    });

    const renderListItems: React.ComponentType<
        UniformSlotWrapperComponentProps
    > = ({ items }) => {
        // Only works for paired dt/dd items. Would need different solution for different structure
        if (listType === "dl") {
            return items.map((item, index) => {
                if (index % 2 === 0) {
                    return (
                        <React.Fragment key={index}>
                            <dt>{item}</dt>
                            {items[index + 1] && <dd>{items[index + 1]}</dd>}
                        </React.Fragment>
                    );
                }
                return null;
            });
        }

        if (listType === "ol") {
            return items.map((item, index) => (
                <li
                    key={index}
                    css={[
                        zeroMarginPadding,
                        css({
                            listStyle: "none",
                            display: "flex",
                            flexDirection: "column",
                            gap: "12px",
                            [media.above.sm]: {
                                flexDirection: "row",
                                alignItems: "baseline"
                            }
                        })
                    ]}
                >
                    <ListItemNumber value={index + 1} />
                    {item}
                </li>
            ));
        }

        return items.map((item, index) => (
            <li
                key={index}
                css={[
                    css({ all: "unset", display: "flex" }),
                    zeroMarginPadding
                ]}
            >
                {item}
            </li>
        ));
    };

    const ListTypeComponent = listType || "ul";

    return (
        <ListTypeComponent
            css={[listStyle, zeroMarginPadding, resetListStyles]}
        >
            <UniformSlot name="listItems" wrapperComponent={renderListItems} />
        </ListTypeComponent>
    );
};

export default UniformListV2;
